import { Box, Chip } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useStoreByName } from '../../../hooks/use-stores.hooks';
import type { SiddurUnit } from '../../../stores/controllers/siddur.controller';
import { HEBREW_LINE } from '../../../utils/hebrew-bidi';
import ScoreStars from './ScoreStars';

/**
 * One screen of the Siddur.
 *
 * A screen holds one to three units, each shown as the breath lines it was
 * split into. The lines are only how it looks: a reading is compared against
 * the unit's whole text, so a line break costs the reader nothing.
 *
 * The text is plain elements rather than MUI Typography on purpose. The global
 * stylesheet sets `.MuiTypography-root { font-family: Heebo !important }`
 * after the font rules, so a Typography cannot carry oprFrank however it is
 * classed. That is why this screen and the reading screen used to disagree
 * about the font of the same prayer.
 */
const SiddurScreenView = () => {
  const { currentScreen, screens, screenIndex, sessionScores } = useStoreByName('siddurStore');

  if (!currentScreen) {
    return (
      <p className="OpenFrankSiddur" dir="rtl" style={{ ...HEBREW_LINE, padding: '48px 0', textAlign: 'center', color: '#6b6b6b' }}>
        אין כאן טקסט לקריאה
      </p>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Which prayer this is. Seven זמירות in a row with no heading read as
          one long song, and a reader has no way to tell where כל מקדש ends
          and מנוחה ושמחה begins. */}
      {currentScreen.prayerName && (
        <p
          dir="rtl"
          className="OpenFrankSiddur"
          style={{
            ...HEBREW_LINE,
            margin: '0 0 4px',
            fontSize: '1.15rem',
            fontWeight: 600,
            color: '#3f7cf6',
          }}
        >
          {currentScreen.prayerName}
        </p>
      )}

      {currentScreen.units.map((unit: SiddurUnit, index: number) => (
        <Box key={unit.node.id} sx={{ ...(index > 0 && { borderTop: '1px solid rgba(0,0,0,0.08)', pt: 2 }) }}>
          {unit.node.name_he && (
            <p
              dir="rtl"
              className="OpenFrankSiddur"
              style={{ ...HEBREW_LINE, margin: '0 0 8px', fontSize: '1rem', color: '#6b6b6b' }}
            >
              {unit.node.name_he}
            </p>
          )}
          {/* How this unit went the last time it was read in this session:
              a row of small stars above its words, so progress is visible
              screen by screen and not only in the star total. */}
          {sessionScores[unit.node.id] !== undefined && (
            <Box dir="rtl" sx={{ mb: 0.5 }}>
              <ScoreStars percentage={sessionScores[unit.node.id]} size={18} />
            </Box>
          )}
          {unit.lines.map((line: string, lineIndex: number) => (
            <p
              key={lineIndex}
              dir="rtl"
              className="OpenFrankSiddur siddur-line"
              style={{ ...HEBREW_LINE, margin: 0, color: '#1a1a1a' }}
            >
              {line}
            </p>
          ))}
        </Box>
      ))}

      <Box dir="rtl" sx={{ display: 'flex', mt: 1 }}>
        <Chip
          size="small"
          variant="outlined"
          label={`${screenIndex + 1} מתוך ${screens.length}`}
        />
      </Box>
    </Box>
  );
};

export default observer(SiddurScreenView);
